import React, { useCallback, useEffect, useState } from 'react';
import { View, Text, TouchableOpacity, ScrollView, StyleSheet } from 'react-native';
import { router } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Search as SearchIcon, MapPin, ChevronRight } from 'lucide-react-native';
import { Header } from '@components/layout';
import { EmptyState } from '@components/common';
import { AddressSearchBar } from '@components/map/AddressSearchBar';
import { useTheme, SPACING, TYPOGRAPHY, CONTENT_MAX_WIDTH, withWeight } from '../../src/theme';
import { useStructuresStore } from '../../src/store/structuresStore';
import { useUIStore } from '../../src/store/uiStore';
import { useAuthStore } from '../../src/store/authStore';
import { getReadinessLevelFromPercent, getReadinessColor } from '../../src/utils/readiness';
import { Structure } from '../../src/types';

function PropertyRow({ structure, onPress }: { structure: Structure; onPress: () => void }) {
  const { colors } = useTheme();
  const pct = structure.claimSummary?.progressPercent;
  const readinessColor =
    pct != null ? (getReadinessColor(getReadinessLevelFromPercent(pct), colors) ?? null) : null;

  return (
    <TouchableOpacity
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={`${structure.address ?? 'Property'}, view details`}
      style={[styles.row, { borderBottomColor: colors.border }]}>
      <MapPin size={18} color={colors.textTertiary} />
      <View style={styles.rowBody}>
        <Text style={[styles.rowAddress, { color: colors.text }]} numberOfLines={1}>
          {structure.address || 'Unknown address'}
        </Text>
        {structure.city ? (
          <Text style={[styles.rowCity, { color: colors.textSecondary }]} numberOfLines={1}>
            {structure.city}
          </Text>
        ) : null}
      </View>
      {pct != null && readinessColor ? (
        <Text style={[styles.rowReadiness, { color: readinessColor }]}>{Math.round(pct)}%</Text>
      ) : null}
      <ChevronRight size={18} color={colors.textTertiary} />
    </TouchableOpacity>
  );
}

export default function SearchScreen() {
  const { colors } = useTheme();
  const insets = useSafeAreaInsets();
  const user = useAuthStore((s) => s.user);
  const { selectStructure, claimedStructureIds, fetchClaimedStructures, fetchStructuresByIds } =
    useStructuresStore();
  const recentlyViewed = useUIStore((s) => s.recentlyViewedStructures);
  const recordStructureView = useUIStore((s) => s.recordStructureView);

  const [claimed, setClaimed] = useState<Structure[]>([]);

  useEffect(() => {
    if (user) fetchClaimedStructures(user.id);
  }, [user, fetchClaimedStructures]);

  useEffect(() => {
    let cancelled = false;
    fetchStructuresByIds(claimedStructureIds.slice(0, 3)).then((results) => {
      if (!cancelled) setClaimed(results);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- store action is stable
  }, [claimedStructureIds]);

  const open = useCallback(
    (structure: Structure) => {
      recordStructureView(structure);
      selectStructure(structure);
      router.push(`/structure/${structure.id}`);
    },
    [recordStructureView, selectStructure]
  );

  const hasSections = recentlyViewed.length > 0 || claimed.length > 0;

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      <Header title="Find a property" large />
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={[styles.content, { paddingBottom: insets.bottom + 100 }]}
        keyboardShouldPersistTaps="handled">
        {/* The search dropdown floats above the sections below it. */}
        <View style={styles.searchWrap}>
          <AddressSearchBar onSelectStructure={open} placeholder="Search by address..." />
        </View>

        {claimed.length > 0 && (
          <View style={styles.section}>
            <Text style={[styles.sectionTitle, { color: colors.textSecondary }]}>
              Your properties
            </Text>
            <View style={[styles.list, { borderTopColor: colors.border }]}>
              {claimed.map((s) => (
                <PropertyRow key={s.id} structure={s} onPress={() => open(s)} />
              ))}
            </View>
          </View>
        )}

        {recentlyViewed.length > 0 && (
          <View style={styles.section}>
            <Text style={[styles.sectionTitle, { color: colors.textSecondary }]}>
              Recently viewed
            </Text>
            <View style={[styles.list, { borderTopColor: colors.border }]}>
              {recentlyViewed.map((s) => (
                <PropertyRow key={s.id} structure={s} onPress={() => open(s)} />
              ))}
            </View>
          </View>
        )}

        {!hasSections && (
          <EmptyState
            compact
            icon={SearchIcon}
            title="Find a property by address"
            message="Look one up to view its readiness, claim it, or add an observation."
            style={styles.empty}
          />
        )}
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  scroll: { flex: 1 },
  content: {
    width: '100%',
    maxWidth: CONTENT_MAX_WIDTH,
    alignSelf: 'center',
    paddingHorizontal: SPACING.md,
    paddingTop: SPACING.md,
  },
  searchWrap: {
    marginBottom: SPACING.lg,
    zIndex: 100,
  },
  section: {
    marginBottom: SPACING.lg,
  },
  sectionTitle: {
    ...TYPOGRAPHY.overline,
    marginBottom: SPACING.sm,
  },
  // Hairline rows, not a bordered card (mockup `.rowlink`).
  list: {
    borderTopWidth: StyleSheet.hairlineWidth,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.smd,
    paddingVertical: SPACING.smd,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  rowBody: {
    flex: 1,
  },
  rowAddress: {
    ...withWeight(TYPOGRAPHY.body, 'medium'),
  },
  rowCity: {
    ...TYPOGRAPHY.bodySmall,
    marginTop: 1,
  },
  rowReadiness: {
    ...TYPOGRAPHY.button,
    fontVariant: ['tabular-nums'],
  },
  empty: {
    marginTop: SPACING.xl,
  },
});
